import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePlanWords } from '../../utils/planWords.js';
import { SeverityBadge, RuleChecks, RuleEditor, RuleSourceChip, BaseUpdateReview } from './RuleFields.jsx';
import {
  Loader2, CheckCircle2, Circle, Ban, Lock, Unlock, Plus, ChevronDown, ChevronRight,
  CheckCircle, ChevronLeft, ChevronsLeft, ChevronsRight, Clock,
} from 'lucide-react';
import {
  listProjectTasks,
  addProjectTask,
  editProjectTask,
  finalizePlanning,
  attestProjectTask,
  approveMilestonePlan,
  fetchTaskMd,
} from '@stepflow/ui-shared/services/dataPlaneApi';
import MarkdownRenderer from '@stepflow/ui-shared/components/MarkdownRenderer.jsx';
import { listPoolMembers } from '@stepflow/ui-shared/services/poolMembersApi.js';
// PROVENANCE ⓘ (§prov 100%-accuracy): the SAME badge the Project map uses — one component, one
// labeling convention (row-level updated/rev vs milestone-level approval), zero drift between tabs.
import { ProvenanceBadge, RevChip, _actorLabel, _fmtTime } from './ProjectMapPanel.jsx';
import { runLayers } from '../../utils/projectPipeline.js';
import { promptDialog } from '@stepflow/ui-shared/components/DialogHost.jsx';

/**
 * TasksTab (§planner) — the project's task ledger review surface, as a TABULAR TREE (mirrors the
 * network-level Playbook tab). A planner run drafts tasks into the canonical ``__planner__`` plan;
 * here the user reads/edits/adds detail + a definition of done, then FINALIZES (draft → pending) so
 * worker rooms may execute. Read-only for non-managers.
 *
 * Layout: milestones are collapsible GROUP headers; each task is a TWO-DECKER row — the top deck
 * spreads the structured fields across columns (ID · Status · Lead · Team · Depends on), the lower
 * deck is a single merged (colSpan) cell with the wordy content (title + detail + DoD) so it
 * stretches the full width instead of crushing into the first column. Edit/add controls live in
 * the lower deck, unchanged in behaviour.
 *
 * Backed by the code-only data-plane routes: GET /tasks, POST /tasks, PATCH /tasks/{id},
 * POST /tasks/finalize.
 */
const STATUS_ICON = {
  done: CheckCircle2,
  blocked: Ban,
  draft: Circle,
  pending: Circle,
  claimed: Loader2,
};

const COLS = [
  { key: 'id', label: 'ID', cls: 'w-px whitespace-nowrap' },
  { key: 'status', label: 'Status', cls: 'w-px whitespace-nowrap' },
  { key: 'lead', label: 'Lead', cls: '' },
  { key: 'team', label: 'Team', cls: '' },   // an Expert Network's "Team" is its group → this column reads "Agents" there
  { key: 'deps', label: 'Depends on', cls: '' },
];

function AgentChips({ agents }) {
  const list = Array.isArray(agents) ? agents.filter(Boolean) : [];
  if (list.length === 0) return <span className="text-gray-300">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {list.map((a) => (
        <span key={a} className="rounded bg-slate-50 px-1.5 py-0.5 text-[10px] text-slate-600 ring-1 ring-slate-200">{a}</span>
      ))}
    </div>
  );
}

// NOTE: the per-task CREDENTIAL-READINESS STRIP (connectors each task's agents need, with a
// [Configure] chip) was REMOVED — the room's Connections tab now owns a single, centralized
// agents↔connectors view (one row per connector + an "Agents" column), so repeating it on every
// milestone/task line was pure clutter. Configure credentials there, not here.

// One task = a TWO-DECKER row: structured columns on top, the wordy title/detail/DoD (+ edit
// controls) merged full-width beneath. Same edit/save behaviour as before, just relocated.
// TASK TIER (§0.1): an epic/parent row carries its subtasks NESTED beneath it — ``childCount`` +
// ``kidsOpen``/``onToggleKids`` render the collapse chevron (default OPEN — the breakdown is the
// value; collapse to skim). A child row (``isChild``) indents under its parent so the epic→subtask
// hierarchy reads visually, matching the Project map tree.
function TaskRow({ task, canManage, finalized, onSave, onAttest, selectable, selected, onToggleSelect, selectForRun = false,
                   childCount = 0, kidsOpen = true, onToggleKids, isChild = false, onLoadMd,
                   onOpenPlanDoc = null, prov = null, resolveUser = null }) {
  const [editing, setEditing] = useState(false);
  const [detail, setDetail] = useState(task.detail || '');
  const [dod, setDod] = useState(task.definitionOfDone || '');
  const [rule, setRule] = useState(task.rule || null);   // EXPERT RULES (E2) — null for a plain task
  const [saving, setSaving] = useState(false);
  // TASK FILE (§task-md): lazy-load + render the small plan .md for THIS task on click (goal · DoD ·
  // ASCII flow · hand-off table) — the same file agents read, shown to the user. Fetched once.
  const [fileOpen, setFileOpen] = useState(false);
  const [fileMd, setFileMd] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileErr, setFileErr] = useState('');
  const toggleFile = async () => {
    const next = !fileOpen;
    setFileOpen(next);
    if (next && fileMd === null && !fileLoading && onLoadMd) {
      setFileLoading(true); setFileErr('');
      try {
        const res = await onLoadMd(task.id);
        setFileMd(String((res && res.markdown) || '') || '_(no file yet — run the plan to generate it)_');
      } catch (e) {
        setFileErr('Could not load the task file.');
      } finally {
        setFileLoading(false);
      }
    }
  };
  const Icon = STATUS_ICON[task.status] || Circle;
  const deps = Array.isArray(task.dependsOn) ? task.dependsOn.filter(Boolean) : [];

  const save = async () => {
    setSaving(true);
    try {
      await onSave(task.id, { detail, definitionOfDone: dod, ...(task.rule ? { rule } : {}) });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  // BASE / OVERLAY (EXPERT-NETWORKS.md §4): answer a base change that arrived under this room's own edit
  const decideBase = async (choice) => {
    setSaving(true);
    try {
      await onSave(task.id, { ruleBase: choice });
    } finally {
      setSaving(false);
    }
  };

  const isParent = childCount > 0;
  return (
    <>
      {/* SUBTASK COLLAPSE (§0.1): the WHOLE parent line is the toggle — click anywhere on the row to
          fold/unfold its children (not just the chevron). Interactive controls inside (chevron,
          groom-select checkbox) stopPropagation so they keep their own behavior. Leaf rows aren't
          clickable. */}
      <tr
        className={`align-top border-t border-gray-100 hover:bg-gray-50/60 ${isParent ? 'cursor-pointer' : ''}`}
        onClick={isParent ? () => onToggleKids?.(task.id) : undefined}
        title={isParent ? (kidsOpen ? 'Collapse subtasks' : 'Expand subtasks') : undefined}
        data-testid={isParent ? `task-row-toggle-${task.id}` : undefined}
      >
        <td className={`w-px whitespace-nowrap px-3 py-2 ${isChild ? 'pl-8' : ''}`}>
          <span className="inline-flex items-center gap-1.5">
            {/* SUBTASK COLLAPSE (§0.1): an epic/parent row folds its children. Chevron + count. */}
            {childCount > 0 ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggleKids?.(task.id); }}
                className="inline-flex items-center gap-0.5 rounded px-0.5 text-[10px] text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-expanded={kidsOpen}
                title={kidsOpen ? `Collapse ${childCount} subtask${childCount === 1 ? '' : 's'}` : `Show ${childCount} subtask${childCount === 1 ? '' : 's'}`}
                data-testid={`task-kids-toggle-${task.id}`}
              >
                {kidsOpen ? '▾' : '▸'}<span className="font-medium">{childCount}</span>
              </button>
            ) : null}
            {isChild ? <span className="text-[10px] text-gray-300" aria-hidden>↳</span> : null}
            {/* GROOM SELECT (§2.6e): pick specific tasks for a surgical groom. Draft/pending only. */}
            {selectable ? (
              <input
                type="checkbox"
                checked={!!selected}
                onChange={() => onToggleSelect?.(task.id)}
                onClick={(e) => e.stopPropagation()}
                className={`h-3 w-3 shrink-0 cursor-pointer ${selectForRun ? 'accent-indigo-600' : 'accent-fuchsia-600'}`}
                aria-label={selectForRun ? `Select ${task.id} to run` : `Select ${task.id} for grooming`}
                data-testid={`task-select-${task.id}`}
              />
            ) : null}
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">{task.id}</span>
            {/* ORIGIN badge (§13 G3.5): template tasks (the network skeleton) are the norm and unbadged;
                an EXPANSION (planner/reconciler/integrator add) is flagged so it's clear which tasks
                came from the template vs were added at runtime. */}
            {task.origin && task.origin !== 'template' ? (
              <span
                className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-200"
                title={`Added at runtime by the ${task.origin} (not from the network template)`}
                data-testid={`task-origin-${task.id}`}
              >
                +{task.origin}
              </span>
            ) : null}
          </span>
        </td>
        <td className="w-px whitespace-nowrap px-3 py-2">
          <span className="inline-flex items-center gap-1">
            <Icon
              className={`h-3.5 w-3.5 shrink-0 ${task.status === 'done' ? 'text-green-600' : task.status === 'blocked' ? 'text-red-500' : task.status === 'claimed' ? 'animate-spin text-sky-500' : 'text-gray-400'}`}
              aria-hidden
            />
            <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-500">{task.status}</span>
          </span>
        </td>
        <td className="px-3 py-2">
          {task.leadAgent
            ? <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-blue-200">{task.leadAgent}</span>
            : <span className="text-gray-300">—</span>}
        </td>
        <td className="px-3 py-2"><AgentChips agents={task.involvedAgents} /></td>
        <td className="px-3 py-2">
          {deps.length > 0
            ? <div className="flex flex-wrap gap-1">{deps.map((d) => (
                <span key={d} className="rounded bg-gray-50 px-1 py-0.5 text-[10px] font-medium text-gray-500">{d}</span>
              ))}</div>
            : <span className="text-gray-300">—</span>}
        </td>
      </tr>
      {/* LOWER DECK — merged full-width: title + detail + DoD, and the edit affordance. A bottom
          border closes the pair so the two rows read as ONE task. */}
      <tr className="border-b border-gray-200 hover:bg-gray-50/60">
        <td colSpan={COLS.length} className={`px-3 pb-2 ${isChild ? 'pl-11' : 'pl-6'}`}>
          <div className="border-l-2 border-gray-100 pl-2">
            <div className="flex items-center gap-2">
              <div className="text-xs font-medium text-gray-800">{task.title || task.id}</div>
              <SeverityBadge rule={task.rule} />
              <RuleSourceChip rule={task.rule} />
              {/* REVISION CHIP (§prov): the per-line edit counter shown INLINE (not only in the ⓘ). */}
              <RevChip rev={task.rev} />
              {/* PROVENANCE ⓘ (§prov): updated-by/-at + rev (row-level) + milestone approval —
                  the same badge/labels as the Project map, so the two tabs can never disagree. */}
              {prov ? <ProvenanceBadge prov={prov} resolveUser={resolveUser} /> : null}
              {/* VIEW TASK FILE (§task-md). DOCK-FIRST (2026-09-26, same UX as Project map → Plan): the
                  plan .md opens as a TAB in the Agent Stream Dock (re-click re-focuses that tab). The
                  inline expand below is only the fallback for a standalone mount with no dock. */}
              {onOpenPlanDoc || onLoadMd ? (
                <button
                  type="button"
                  onClick={onOpenPlanDoc ? () => onOpenPlanDoc(task.id, task.title || task.id) : toggleFile}
                  className="shrink-0 rounded px-1 py-0.5 text-[10px] font-medium text-indigo-500 hover:bg-indigo-50"
                  aria-expanded={onOpenPlanDoc ? undefined : fileOpen}
                  data-testid={`task-md-toggle-${task.id}`}
                  title={onOpenPlanDoc ? "Open this task's plan file in the dock — goal, flow diagram, hand-offs"
                    : "View this task's plan file — goal, flow diagram, hand-offs"}
                >
                  {!onOpenPlanDoc && fileOpen ? '▾ file' : '📄 file'}
                </button>
              ) : null}
            </div>
            {fileOpen && !onOpenPlanDoc ? (
              <div className="mt-1 rounded-md border border-indigo-100 bg-indigo-50/30 px-3 py-2" data-testid={`task-md-panel-${task.id}`}>
                {fileLoading ? <div className="text-[11px] text-gray-400">loading…</div>
                  : fileErr ? <div className="text-[11px] text-red-500">{fileErr}</div>
                    : <MarkdownRenderer content={fileMd || ''} className="text-[12px]" />}
              </div>
            ) : null}
            {editing ? (
              <div className="mt-2 max-w-2xl space-y-2">
                <textarea
                  value={detail}
                  onChange={(e) => setDetail(e.target.value)}
                  placeholder="What exactly should this task do?"
                  className="w-full rounded border border-gray-300 px-2 py-1 text-[12px]"
                  rows={2}
                />
                <textarea
                  value={dod}
                  onChange={(e) => setDod(e.target.value)}
                  placeholder="Definition of done — the acceptance bar"
                  className="w-full rounded border border-gray-300 px-2 py-1 text-[12px]"
                  rows={2}
                />
                {task.rule ? <RuleEditor value={rule} onChange={setRule} /> : null}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors text-gray-600 hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                {task.detail ? <p className="mt-0.5 max-w-2xl text-[11px] leading-relaxed text-gray-600">{task.detail}</p> : null}
                {task.definitionOfDone ? (
                  <p className="mt-1 max-w-2xl text-[11px] text-gray-500">
                    <span className="font-medium">DoD:</span> {task.definitionOfDone}
                  </p>
                ) : null}
                <RuleChecks rule={task.rule} />
                <BaseUpdateReview rule={task.rule} busy={saving}
                  onDecide={canManage ? decideBase : undefined} />
                {/* §2.7: the task's OUTPUT ADDRESS — where its deliverable is written + groomed.
                    Deterministic (LeadAgent/Milestone/TaskId); the same address whether a worker or
                    builder fills it. Shown so grooming knows exactly which folder it regenerates. */}
                {Array.isArray(task.outputFiles) && task.outputFiles[0] ? (
                  <p className="mt-1 text-[11px] text-gray-500" title="Deterministic task output address (agnostic to worker/builder)">
                    <span className="font-medium">Output:</span>{' '}
                    <code className="rounded bg-gray-100 px-1 py-0.5 text-[10px] text-gray-700">{task.outputFiles[0]}</code>
                  </p>
                ) : null}
                {canManage && !finalized ? (
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="mt-1 text-[11px] font-medium text-blue-600 hover:underline"
                  >
                    Edit detail
                  </button>
                ) : null}
                {/* CLOSURE (§E): a manually-attested done reads distinctly from a tool-verified one
                    (never launder attested as verified). An open task (finalized, not done) offers a
                    manual "Attest done" for cases no tool can prove. */}
                {task.status === 'done' && task.closedBy === 'user' ? (
                  <p className="mt-1 text-[11px] text-violet-600" title={task.closedReason || undefined}>
                    ✓ done — manually attested{task.attestedBy ? ` by ${task.attestedBy}` : ''}
                  </p>
                ) : null}
                {(task.waitingOn || []).length && task.status !== 'done' ? (
                  <p className="mt-1 text-[11px] text-amber-700" data-testid={`task-waiting-${task.id}`}>
                    ⏸ waiting on you: {task.waitingOn.slice(0, 3).join('; ')}{task.waitingOn.length > 3 ? ' …' : ''} (Project map → Gaps)
                  </p>
                ) : null}
                {/* VERIFY (G0b): the agents' check of the uploaded proof — the human then attests. */}
                {task.verification && task.status !== 'done' ? (
                  <p data-testid={`task-verification-${task.id}`}
                     className={`mt-1 text-[11px] ${task.verification.verdict === 'verified' ? 'text-emerald-700' : 'text-amber-700'}`}
                     title={[...(task.verification.gaps || []).map((g) => `• ${g}`),
                       task.verification.evidence ? `proof: ${task.verification.evidence}` : ''].filter(Boolean).join('\n') || undefined}>
                    {task.verification.verdict === 'verified'
                      ? '✓ agents checked the proof — every part of the definition of done is met'
                      : task.verification.verdict === 'gaps'
                        ? `⚠ agents checked the proof — ${(task.verification.gaps || []).length || 'some'} gap(s): ${(task.verification.gaps || []).slice(0, 2).join('; ')}`
                        : 'agents checked the proof — no clear verdict; review it yourself'}
                  </p>
                ) : null}
                {canManage && finalized && task.status !== 'done' && task.status !== 'blocked' && onAttest ? (
                  <button
                    type="button"
                    onClick={() => onAttest(task)}
                    className="mt-1 ml-3 text-[11px] font-medium text-violet-600 hover:underline"
                    title="Close this task manually with a note/evidence (for work no tool can prove)"
                  >
                    Attest done
                  </button>
                ) : null}
              </>
            )}
          </div>
        </td>
      </tr>
    </>
  );
}


// (L122) Human wave label: waves are 1-BASED in data (``derive_waves``) — show them as-is. A LEGACY plan that
// still carries a wave 0 is 0-based → shift by one so the user always reads "wave 1" first.
export function waveLabel(w, allWaves) {
  const n = Number(w) || 0;
  return (Array.isArray(allWaves) && allWaves.some((x) => Number(x) === 0)) ? n + 1 : n;
}

export default function TasksTab({ networkId, poolId, canManage = false, onGroom,
  planTier = 'auto', onPlanTier, active = true, onPlanAllWaves, onStopAutoWave,
  // PLAN LIVE (§2.5g): a bump from the room when a PLAN_UPDATED beacon arrives → quiet-refresh so the
  // plan reflects a run's capture / a peer's approve without a manual refresh.
  planUpdatedSignal = 0,
  // ASYNC PLAN-UPDATE (2026-09-05): true while the async plan-update lambda is detailing drafts (between
  // a PLAN_UPDATE_RUNNING beacon and the terminal PLAN_UPDATED) → show a 'plan updating…' banner so the
  // user knows the (possibly stale) tasks below are still being authored in the background.
  planUpdating = false,
  // FOCUS REQUEST ({ id/name, nonce }): the composer's "Review" jump asks to reveal a specific
  // milestone — expand it + scroll it into view. ``nonce`` makes repeated jumps to the SAME milestone
  // re-fire (a new object each time). Null → no focus.
  focusRequest = null,
  // NO BLACK BOX (L60): jump to the live "Planner" stream from the plan-updating banner.
  onOpenPlannerStream = null,
  // DOCK-FIRST task file (2026-09-26): open a task's plan .md as an Agent Stream Dock tab — the SAME
  // path the Project map's Plan button uses (openPlanDocInDock). Null → inline fallback.
  onOpenPlanDoc = null,
  // EXPERT ROOMS (EXPERT-ROOMS.md §4, X2): a DEPARTMENT room of an expert team opens on ITS Team (the plan is shared
  // with the Lead + the other departments); a toggle shows every Team. '' → every Team (unchanged).
  teamFocus = '' }) {
  const w = usePlanWords();
  const [onlyMyTeam, setOnlyMyTeam] = useState(true);
  const teamKey = String(teamFocus || '').trim().toLowerCase();   // EXPERT-NETWORKS §1 (E1): the plan's words — Milestone/Epic/Task or the network's own
  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());
  // GROOM SELECTION (§2.6e / §2.8): the milestone + the partition the user picked for a surgical
  // groom — EITHER specific task ``ids`` (row select) OR concurrency ``waves`` (wave-bounded). Both
  // are DETERMINISTIC and mutually exclusive; confined to ONE milestone. Handed to ``onGroom`` → the
  // composer's groomScope ({milestone, taskIds} or {milestone, waves}).
  const [groomSel, setGroomSel] = useState({ milestone: '', ids: new Set(), waves: new Set(), epic: '', epicStory: '' });

  // Pool roster (sub → workspace name/email) so the provenance ⓘ shows "Jane Doe", never a raw
  // Cognito UUID — the same join ExecutionsTab / ProjectMapPanel use. Best-effort.
  const [usersBySub, setUsersBySub] = useState({});
  useEffect(() => {
    if (!networkId || !poolId) { setUsersBySub({}); return undefined; }
    let cancelled = false;
    listPoolMembers(networkId, poolId)
      .then((res) => {
        if (cancelled) return;
        const map = {};
        for (const mem of (res?.members || [])) {
          const sub = String(mem?.userSub || '').trim();
          if (!sub) continue;
          const name = String(mem?.userName || '').trim();
          const email = String(mem?.email || '').trim();
          if (name || email) map[sub] = name || email;
        }
        setUsersBySub(map);
      })
      .catch(() => { if (!cancelled) setUsersBySub({}); });
    return () => { cancelled = true; };
  }, [networkId, poolId]);
  const resolveUser = useCallback((sub) => usersBySub[String(sub || '').trim()] || null, [usersBySub]);

  // LAZY LOADING (perf): fetch the LIGHT index (milestone shells + counts + light tasks — no heavy
  // detail/DoD text). Full task bodies load PER MILESTONE on expand (``fullByMs`` cache below). This
  // keeps a big plan's Tasks tab fast: only the pending/cursor milestone's bodies load up front.
  const load = useCallback(() => {
    if (!networkId) return;
    setState((s) => ({ ...s, loading: true, error: '' }));
    listProjectTasks(networkId, { view: 'index', ...(poolId ? { pool: poolId } : {}) })
      .then((data) => setState({ loading: false, error: '', data }))
      .catch(() => setState({ loading: false, error: 'Couldn’t load the plan.', data: null }));
  }, [networkId, poolId]);

  // QUIET REFRESH (UX §tab-persist): re-fetch the index WITHOUT the loading flash + WITHOUT touching
  // the user's selection. ``groomSel`` / ``planTier`` / ``collapsed`` are preserved. Already-expanded
  // milestones' full bodies are re-fetched too (below) so an edit/groom shows fresh detail.
  const refresh = useCallback(() => {
    if (!networkId) return;
    listProjectTasks(networkId, { view: 'index', ...(poolId ? { pool: poolId } : {}) })
      .then((data) => {
        setState((s) => ({ ...s, loading: false, error: '', data }));
        // Re-fetch the full bodies of any milestone the user already expanded, so an edit/groom/
        // approve shows fresh detail (not a stale cached body). Uses the index's id↔name mapping.
        setFullByMs((prev) => {
          const open = Object.keys(prev);
          if (open.length) {
            const idByName = {};
            for (const m of (data?.milestones || [])) idByName[m.name] = m.id;
            open.forEach((nm) => loadMilestone(idByName[nm], nm));
          }
          return prev;   // loadMilestone updates the cache async; keep current until it resolves
        });
      })
      .catch(() => { /* keep the current view on a transient refresh error */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkId, poolId]);

  // PER-MILESTONE FULL BODIES cache (keyed by milestone NAME) + in-flight set. Populated lazily when
  // a milestone is expanded (or auto-loaded for the cursor milestone). null entry = not yet loaded.
  const [fullByMs, setFullByMs] = useState({});          // { [name]: Task[] }
  const [loadingMs, setLoadingMs] = useState(() => new Set());
  // LOCAL PAGINATION (perf/UX) — a header paginator (<< < > >> + page-size input + All) narrows the
  // VIEW to N tasks at a time, purely client-side (no fetch). Disconnected from waves — it's a plain
  // task-count window. ``pageByMs[name]`` = current 0-based page; ``sizeByMs[name]`` = per-milestone
  // page size (default DEFAULT_PAGE_SIZE; Infinity = All). Independent per milestone.
  const DEFAULT_PAGE_SIZE = 3;
  const [pageByMs, setPageByMs] = useState({});          // { [name]: number }  (0-based page index)
  const [sizeByMs, setSizeByMs] = useState({});          // { [name]: number | Infinity }
  // SUBTASK COLLAPSE (§0.1): parent task ids whose children are folded. DEFAULT OPEN (the breakdown
  // is the value; collapse to skim) — so the set holds the EXCEPTIONS.
  const [foldedKids, setFoldedKids] = useState(() => new Set());
  const toggleKids = (tid) => setFoldedKids((prev) => {
    const next = new Set(prev);
    if (next.has(tid)) next.delete(tid); else next.add(tid);
    return next;
  });
  const loadMilestone = useCallback((mId, mName) => {
    if (!networkId || !mName) return;
    setLoadingMs((prev) => { const n = new Set(prev); n.add(mName); return n; });
    listProjectTasks(networkId, { milestone: mId || mName, ...(poolId ? { pool: poolId } : {}) })
      .then((data) => {
        const rows = (Array.isArray(data?.tasks) ? data.tasks : []).filter((t) => t && t.milestone === mName);
        setFullByMs((prev) => ({ ...prev, [mName]: rows }));
      })
      .catch(() => { /* leave the light shell; a retry (re-expand) can refetch */ })
      .finally(() => setLoadingMs((prev) => { const n = new Set(prev); n.delete(mName); return n; }));
  }, [networkId, poolId]);

  // LOAD ON FIRST OPEN (L164): the panel stays mounted (``unmount={false}``), so loading on mount fired a second
  // full plan read (~2 s) on EVERY room open even when nobody looks at Tasks — the room already reads the plan state
  // it needs for the composer. Now the first load waits until the tab is active (and re-runs for a new network).
  const _loadedFor = useRef(null);
  const _skipActiveRefresh = useRef(false);
  useEffect(() => {
    if (!active || _loadedFor.current === load) return;
    _skipActiveRefresh.current = _mountedRef.current;   // after mount, the activation's refresh would double it
    _loadedFor.current = load;
    load();
  }, [load, active]);

  // AUTO-LOAD the pending/cursor milestone's full bodies once the index lands — that's the milestone
  // the user works on first (open by default), so its detail should be ready without a click.
  const _autoLoadedRef = useRef('');
  useEffect(() => {
    const ms = state.data?.milestones;
    const cursor = state.data?.planningCursor;
    if (!Array.isArray(ms) || !cursor) return;
    const m = ms.find((x) => x.id === cursor);
    if (!m || _autoLoadedRef.current === m.name) return;
    // Has it any tasks? Prefer the index taskCount; fall back to the light rows already present.
    const hasTasks = (m.taskCount || 0) > 0
      || (state.data.tasks || []).some((t) => t.milestone === m.name);
    if (!hasTasks) return;
    _autoLoadedRef.current = m.name;
    if (!fullByMs[m.name]) loadMilestone(m.id, m.name);
  }, [state.data, fullByMs, loadMilestone]);

  // COLLAPSE-ALL-BUT-CURSOR (perf/UX): on the FIRST index load, fold EVERY milestone except the
  // planning cursor (e.g. ARCHITECTURE). Only the cursor is open + auto-loaded; all others (approved
  // AND future pending) are closed shells that render nothing and load their bodies only on expand.
  // This is what keeps the Tasks tab fast — a big plan no longer renders ~40 rows on open. Runs ONCE
  // per network (a ref-latched guard) so the user's manual expand/collapse afterwards sticks.
  const _initialFoldedRef = useRef('');
  useEffect(() => {
    const ms = state.data?.milestones;
    if (!Array.isArray(ms) || !ms.length) return;
    if (_initialFoldedRef.current === networkId) return;      // already did the initial fold
    _initialFoldedRef.current = networkId;
    const cursor = state.data?.planningCursor;
    const cursorName = ms.find((x) => x.id === cursor)?.name;
    // Keep ONLY the cursor open; if there's no cursor (all approved), keep the LAST milestone open.
    const keepOpen = cursorName || ms[ms.length - 1].name;
    setCollapsed(new Set(ms.map((m) => m.name).filter((n) => n !== keepOpen)));
  }, [state.data, networkId]);

  // On BECOMING the active tab (panel is kept mounted via ``unmount={false}``), quietly refresh so
  // the plan isn't stale — without wiping selection. Skip the very first mount (``load`` covers it).
  const _mountedRef = useRef(false);
  useEffect(() => {
    if (!_mountedRef.current) { _mountedRef.current = true; return; }
    if (!active) return;
    if (_skipActiveRefresh.current) { _skipActiveRefresh.current = false; return; }   // the first load just ran
    refresh();
  }, [active, refresh]);

  // PLAN LIVE (§2.5g): a PLAN_UPDATED beacon bumped ``planUpdatedSignal`` → quiet-refresh the index +
  // re-pull expanded milestones (``refresh`` already does both). Skip the initial 0 (load covers it).
  const _lastSignalRef = useRef(0);
  useEffect(() => {
    if (planUpdatedSignal && planUpdatedSignal !== _lastSignalRef.current) {
      _lastSignalRef.current = planUpdatedSignal;
      refresh();
    }
  }, [planUpdatedSignal, refresh]);

  const data = state.data;
  const finalized = !!data?.finalized;
  const msTotal = (data?.milestones || []).length;
  const lockedCount = (data?.milestones || []).filter((m) => m.locked).length;
  // Group tasks by milestone name, PREFERRING the lazily-loaded FULL bodies (``fullByMs``) over the
  // light index rows. A collapsed/unloaded milestone still has its light shells (title/status/wave)
  // from the index, so headers + chips render immediately; detail/DoD appear once expanded-loaded.
  const byMilestone = useMemo(() => {
    const groups = {};
    for (const t of data?.tasks || []) {
      (groups[t.milestone] = groups[t.milestone] || []).push(t);
    }
    // Overlay full bodies where we have them.
    for (const [name, rows] of Object.entries(fullByMs)) {
      if (Array.isArray(rows) && rows.length) groups[name] = rows;
    }
    return groups;
  }, [data, fullByMs]);

  // FOCUS a milestone on request (composer "Review" jump): map its NAME → the group's DOM node so we
  // can expand + scroll it. The request may carry an id (M{i}) or a name; resolve to the name.
  const milestoneRefs = useRef({});
  useEffect(() => {
    if (!focusRequest || !data) return;
    const ms = (data.milestones || []).find(
      (m) => m.id === focusRequest.id || m.name === focusRequest.name || m.name === focusRequest.id);
    const name = ms?.name || focusRequest.name;
    if (!name) return;
    // Expand it (remove from collapsed set) then scroll after the row renders.
    setCollapsed((prev) => {
      if (!prev.has(name)) return prev;
      const next = new Set(prev); next.delete(name); return next;
    });
    // LAZY: a Review jump reveals a milestone → load its full bodies if not cached.
    if (ms && !fullByMs[name]) loadMilestone(ms.id, name);
    setTimeout(() => {
      milestoneRefs.current[name]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  }, [focusRequest, data, fullByMs, loadMilestone]);

  // PRUNE STALE SELECTION (UX §tab-persist): after a quiet refresh, drop any groom-selected task id
  // that no longer exists in the plan (e.g. it was executed/removed elsewhere) so we never carry a
  // stale id into a run. A still-present selection is untouched. Wave selection needs no prune (a
  // wave is a number, not an id). ``finalized`` also clears selection (grooming is pre-finalize).
  useEffect(() => {
    // a DONE task drops out too (a Run tasks selection shrinks as its tasks finish)
    const live = new Set((data?.tasks || []).filter((t) => t.status !== 'done').map((t) => t.id));
    setGroomSel((prev) => {
      if (finalized && prev.waves.size) {   // wave grooming is pre-finalize; a row selection is now a RUN selection
        return { milestone: '', ids: new Set(), waves: new Set(), epic: '', epicStory: '' };
      }
      if (!prev.ids.size) return prev;
      const kept = new Set([...prev.ids].filter((id) => live.has(id)));
      if (kept.size === prev.ids.size) return prev;   // nothing pruned → no re-render
      return { milestone: kept.size ? prev.milestone : '', ids: kept, waves: prev.waves };
    });
  }, [data, finalized]);

  const onSave = async (taskId, patch) => {
    await editProjectTask(networkId, taskId, poolId ? { ...patch, poolId } : patch);
    load();
  };

  // MANUAL ATTESTATION (§E): close a task no tool can prove, with a required note. (Upload-OR-note;
  // v1 uses a note via prompt — evidence upload rides the same route's evidenceKey when wired.)
  const handleAttest = useCallback(async (task) => {
    if (!task?.id) return;
    // VERIFY (G0b): when the agents checked uploaded proof, attach THAT evidence to the attestation.
    const v = task.verification || null;
    const evidenceKey = (v && v.evidence) || '';
    const suggested = v && v.verdict === 'verified' ? 'Proof checked by the agents — every part of the definition of done is met.' : '';
    // eslint-disable-next-line no-alert
    const note = await promptDialog({ title: `Attest "${task.title || task.id}" as done`,
      message: `${evidenceKey ? `Proof attached: ${evidenceKey}\n` : ''}Add a note — what proves it's complete:`,
      defaultValue: suggested || '', confirmLabel: 'Attest done' });
    if (note == null) return;                 // cancelled
    if (!String(note).trim()) return;         // note required (upload-or-note; here: note)
    setBusy(true);
    try {
      await attestProjectTask(networkId, task.id, { note: String(note).trim(), ...(evidenceKey ? { evidenceKey } : {}),
        ...(poolId ? { pool: poolId } : {}) });
      load();
    } finally {
      setBusy(false);
    }
  }, [networkId, poolId, load]);
  // Lazy per-task .md loader (§task-md) — rendered from the ledger on demand when a row is opened.
  const loadTaskMd = useCallback((tid) => fetchTaskMd(networkId, tid, poolId ? { pool: poolId } : {}),
    [networkId, poolId]);

  // LOCK (P5a, PLAN-TO-EXECUTE §6) — per milestone, replacing the global "Finalize plan": gated on an
  // approved plan + every open task READY; a refusal lists the exceptions and offers "Lock anyway"
  // (recorded on the lock). Only that milestone's drafts become runnable; its tasks freeze until Unlock.
  const [lockBlock, setLockBlock] = useState({});     // {milestoneId: {message, exceptions}}
  const onLock = async (milestoneId, lock, acceptExceptions = false) => {
    setBusy(true);
    try {
      await finalizePlanning(networkId, { finalized: lock, milestone: milestoneId,
        ...(acceptExceptions ? { acceptExceptions: true } : {}), ...(poolId ? { pool: poolId } : {}) });
      setLockBlock((prev) => { const n = { ...prev }; delete n[milestoneId]; return n; });
      // Reflect the confirmed lock at once (the re-fetch below then refreshes statuses + chips).
      setState((st) => (st.data ? { ...st, data: { ...st.data, milestones: (st.data.milestones || []).map((m) => (
        m.id === milestoneId ? { ...m, locked: !!lock } : m)) } } : st));
      refresh();                                        // QUIET — no loading flash hiding the new state
    } catch (e) {
      const d = (e && e.data) || {};
      setLockBlock((prev) => ({ ...prev, [milestoneId]: {
        message: d.message || (e && e.message) || 'could not lock', exceptions: d.exceptions || [],
        needs: d.needs || [] } }));      // G4: essential items missing → NO "Lock anyway" (answer them in Gaps)
    } finally {
      setBusy(false);
    }
  };

  // PLANNING CURSOR (§2.5): approve a milestone's plan → the planning cursor advances so the NEXT
  // plan run scopes to the next milestone. Distinct from finalize (which opens execution).
  const planningCursor = data?.planningCursor || '';
  const onApprovePlan = async (milestoneId, approve) => {
    setBusy(true);
    try {
      await approveMilestonePlan(networkId, milestoneId, { approved: approve, pool: poolId });
      load();
    } finally {
      setBusy(false);
    }
  };

  const toggle = (name, mId) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
    // LAZY: EXPANDING (was collapsed) an unloaded milestone → fetch its full bodies now.
    if (collapsed.has(name) && !fullByMs[name]) loadMilestone(mId, name);
  };

  // GROOM (§2.6e): toggle a task into the groom set. Selection is single-milestone — picking a task
  // in a different milestone starts a fresh set (a groom run scopes to ONE milestone). Row-select and
  // wave-select are mutually exclusive (picking rows clears any wave selection).
  const toggleGroom = useCallback((milestoneId, taskId) => {
    setGroomSel((prev) => {
      const sameMs = prev.milestone === milestoneId;
      const ids = new Set(sameMs ? prev.ids : []);
      if (ids.has(taskId)) ids.delete(taskId); else ids.add(taskId);
      return { milestone: ids.size ? milestoneId : '', ids, waves: new Set(), epic: '', epicStory: '' };
    });
  }, []);
  // GROOM (§2.8): toggle a WAVE into the partition. Single-milestone; clears any row/epic selection.
  const toggleWave = useCallback((milestoneId, wave) => {
    setGroomSel((prev) => {
      const sameMs = prev.milestone === milestoneId;
      const waves = new Set(sameMs ? prev.waves : []);
      if (waves.has(wave)) waves.delete(wave); else waves.add(wave);
      return { milestone: waves.size ? milestoneId : '', ids: new Set(), waves, epic: '', epicStory: '' };
    });
  }, []);
  // EPIC EXPANSION (§0.1): select ONE epic to break into its granular executable tasks — the direct
  // sibling of wave-select. Single-select (expand one epic at a time); clears any row/wave selection.
  // Toggling the same epic off clears the selection.
  const toggleEpic = useCallback((milestoneId, epicId, epicStory) => {
    setGroomSel((prev) => {
      const on = prev.milestone === milestoneId && prev.epic === epicId;
      return on
        ? { milestone: '', ids: new Set(), waves: new Set(), epic: '', epicStory: '' }
        : { milestone: milestoneId, ids: new Set(), waves: new Set(), epic: epicId, epicStory: epicStory || '' };
    });
  }, []);
  const clearGroom = useCallback(() => setGroomSel({ milestone: '', ids: new Set(), waves: new Set(), epic: '', epicStory: '' }), []);

  // Hand the current selection to the room (→ composer groomScope) whenever it changes. onClear lets
  // the composer chip reset it. Empty → null (composer falls back to whole-milestone plan). Row select
  // → {taskIds}; wave select → {waves} (the runner expands waves→taskIds deterministically, §2.8).
  useEffect(() => {
    if (typeof onGroom !== 'function') return;
    let scope = null;
    // Carry the milestone NAME + a KIND so the composer chip reads consistently (name not id,
    // right verb): row-select = "Refine" (edit an existing plan); wave-select = "Plan" (bounded
    // planning of a big milestone). ``milestone`` stays the id (the run scope key).
    const mName = (state.data?.milestones || []).find((x) => x.id === groomSel.milestone)?.name
      || groomSel.milestone;
    const msRow = (state.data?.milestones || []).find((x) => x.id === groomSel.milestone);
    if (groomSel.milestone && groomSel.ids.size && msRow && (msRow.locked || state.data?.finalized)) {
      // RUN TASKS (MODES.md §2, M3): a selection in a LOCKED milestone is a RUN selection, not a groom —
      // the composer offers "Run tasks (k)" with the dependency order (linked in series, the rest parallel).
      const ids = [...groomSel.ids];
      scope = { milestone: groomSel.milestone, milestoneName: mName, kind: 'run', taskIds: ids,
        layers: runLayers(ids, state.data?.tasks || []), onClear: clearGroom };
    } else if (groomSel.milestone && groomSel.epic) {
      // EPIC EXPANSION (§0.1): author granular leaf tasks under this epic (only leaves execute). The
      // composer prefills "Create tasks for {M} → {story}"; the runner keys on ``epicParent``.
      scope = { milestone: groomSel.milestone, milestoneName: mName, kind: 'epic',
        epicParent: groomSel.epic, epicStory: groomSel.epicStory, onClear: clearGroom };
    } else if (groomSel.milestone && groomSel.ids.size) {
      scope = { milestone: groomSel.milestone, milestoneName: mName, kind: 'refine',
        taskIds: [...groomSel.ids], onClear: clearGroom };
    } else if (groomSel.milestone && groomSel.waves.size) {
      scope = { milestone: groomSel.milestone, milestoneName: mName, kind: 'wave',
        waves: [...groomSel.waves].sort((a, b) => a - b), onClear: clearGroom };
    }
    onGroom(scope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groomSel, onGroom, clearGroom]);

  const [adding, setAdding] = useState(null); // milestone name or null
  const [newTask, setNewTask] = useState({ title: '', leadAgent: '', detail: '', definitionOfDone: '' });
  const submitAdd = async (milestone) => {
    if (!newTask.title.trim() || !newTask.leadAgent.trim()) return;
    setBusy(true);
    try {
      await addProjectTask(networkId, poolId ? { ...newTask, milestone, poolId } : { ...newTask, milestone });
      setNewTask({ title: '', leadAgent: '', detail: '', definitionOfDone: '' });
      setAdding(null);
      load();
    } catch {
      /* validation error surfaces on reload / no-op */
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) {
    return (
      <div className="flex flex-1 items-center justify-center text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
      </div>
    );
  }
  if (state.error) {
    return <div className="p-4 text-sm text-red-500">{state.error}</div>;
  }
  if (!data?.eligible) {
    return (
      <div className="p-4 text-sm text-gray-500">
        This network isn’t a project (no milestones), so there’s no plan to review.
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-auto p-4">
      {planUpdating ? (
        <div className="mb-3 flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-[12px] text-blue-700" data-testid="tasks-plan-updating">
          <span className="h-2 w-2 animate-pulse rounded-full bg-blue-500" />
          Plan updating… tasks are being detailed in the background — this list will refresh when it finishes.
          {/* NO BLACK BOX (L60): the async planner streams live as the "Planner" builder card —
              this agent-style chip jumps the user to that stream so they WATCH the plan being
              authored, exactly like clicking any builder agent. Only rendered when the room wires
              the jump (project rooms). */}
          {onOpenPlannerStream ? (
            <button
              type="button"
              onClick={onOpenPlannerStream}
              className="ml-auto shrink-0 inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-700 ring-1 ring-blue-300 hover:bg-blue-200"
              data-testid="tasks-open-planner-stream"
              title="Watch the Planner agent working live in the stream"
            >
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-600" />
              Planner agent
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[13px] font-medium text-gray-800">
          {lockedCount ? <Lock className="h-4 w-4 text-green-600" aria-hidden /> : <Unlock className="h-4 w-4 text-amber-500" aria-hidden />}
          <span data-testid="tasks-lock-summary">
            {lockedCount
              ? `${lockedCount} of ${msTotal} ${msTotal === 1 ? w.groupLc : w.groupsLc} locked — locked ${w.groupsLc} can run`
              : `Draft plan — approve each ${w.groupLc}, then lock it to run`}
          </span>
          {/* PLAN REVISION (§prov): the plan-level changelog head — rev + who/when/why last changed
              the plan. Hidden for a legacy plan never bumped. */}
          {data.planRev && data.planRev.rev ? (
            <span className="text-[10px] font-normal text-gray-400" data-testid="tasks-plan-rev">
              rev {data.planRev.rev}
              {data.planRev.reason ? ` · ${data.planRev.reason}` : ''}
              {' · '}{_actorLabel(data.planRev.updatedBy, resolveUser)}
              {data.planRev.updatedAt ? ` · ${_fmtTime(data.planRev.updatedAt)}` : ''}
            </span>
          ) : null}
        </div>
        {/* The global "Finalize plan" button was REPLACED by a per-milestone Lock (P5a, 2026-09-29). */}
      </div>

      {/* CROSS-MILESTONE integrity panel REMOVED (2026-09-17): the raw ``unmet_require``/
          ``backward_handoff`` break list is INTERNAL plan-graph QA, not user-approval info — it read as
          a frightening "N unconnected" wall, and most breaks were false alarms from LLM require/provide
          key-naming drift. That drift is now DETERMINISTICALLY pruned at plan-apply
          (project_ledger.prune_dangling_requires) so the plan is connected by construction; a genuine
          residual gap becomes a quiet advisory, never a red panel. The user reviews the connected
          tree + grooms — they don't debug the walker. (Backend still computes + persists the verdict.) */}

      <div className="space-y-2">
        {teamKey ? (
          <div className="flex justify-end" data-testid="tasks-team-focus">
            <button type="button" onClick={() => setOnlyMyTeam((v) => !v)}
              className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors text-gray-600 hover:bg-gray-100">
              {onlyMyTeam ? `Your team (${teamFocus}) · show all teams` : `All teams · show only ${teamFocus}`}
            </button>
          </div>
        ) : null}
        {(data.milestones || []).filter((m) => !teamKey || !onlyMyTeam
          || String(m.name || '').trim().toLowerCase() === teamKey).map((m) => {
          const tasks = byMilestone[m.name] || [];
          // LOCK (P5a): a locked milestone is frozen (no edit/add/select/reopen) — per milestone, so the
          // others stay groomable. ``finalized`` (legacy whole-plan flag) still freezes everything.
          const msLocked = !!m.locked;
          const frozen = finalized || msLocked;
          const open = !collapsed.has(m.name);
          const planApproved = !!m.planApproved;
          const isCursor = m.id && m.id === planningCursor;
          // AWAITING APPROVAL (§13 G3.5): the synthesizer fully planned this milestone and it's not
          // yet approved → prompt the human's per-milestone Approve gate.
          const awaitingApproval = !!m.awaitingApproval && !planApproved;
          const hasDetailed = tasks.some((t) => (t.detail || '').trim() || (t.definitionOfDone || '').trim());
          // LAZY: count from the index shell (always present); a spinner shows while full bodies load.
          const msCount = typeof m.taskCount === 'number' ? m.taskCount : tasks.length;
          const msLoading = loadingMs.has(m.name);
          // TASK TIER (§0.1): group leaf subtasks UNDER their parent epic/task — the table mirrors
          // the Project-map tree (epic row + nested, collapsible children) instead of a flat list
          // where M0-004.2 floats away from M0-004. Orphan children (parent filtered/absent) stay
          // top-level so nothing is ever hidden.
          const parentIds = new Set(tasks.map((t) => t.id));
          const kidsByParent = {};
          for (const t of tasks) {
            const p = (t.parentTaskId || '').trim();
            if (p && parentIds.has(p)) (kidsByParent[p] = kidsByParent[p] || []).push(t);
          }
          const topTasks = tasks.filter((t) => !((t.parentTaskId || '').trim() && parentIds.has((t.parentTaskId || '').trim())));
          // LOCAL PAGINATION (header paginator): window the TOP-LEVEL rows (children ride with their
          // parent — a page never splits an epic from its subtasks).
          const pageSize = sizeByMs[m.name] ?? DEFAULT_PAGE_SIZE;              // number | Infinity
          const pageCount = pageSize === Infinity ? 1 : Math.max(1, Math.ceil(topTasks.length / pageSize));
          const page = Math.min(pageByMs[m.name] ?? 0, pageCount - 1);        // clamp (e.g. after size↑)
          const visibleTasks = pageSize === Infinity
            ? topTasks
            : topTasks.slice(page * pageSize, page * pageSize + pageSize);
          const paginated = topTasks.length > 0 && pageSize !== Infinity && topTasks.length > pageSize;
          const setPage = (p) => setPageByMs((prev) => ({ ...prev, [m.name]: Math.max(0, Math.min(p, pageCount - 1)) }));
          const setSize = (n) => {
            setSizeByMs((prev) => ({ ...prev, [m.name]: n }));
            setPageByMs((prev) => ({ ...prev, [m.name]: 0 }));   // reset to first page on size change
          };
          return (
            <div
              key={m.name}
              ref={(el) => { milestoneRefs.current[m.name] = el; }}
              className="overflow-hidden rounded-md border border-gray-200 bg-white"
            >
              <div className="flex w-full items-center gap-2 bg-gray-50 px-3 py-2 hover:bg-gray-100">
                <button
                  type="button"
                  onClick={() => toggle(m.name, m.id)}
                  className="flex min-w-0 flex-1 items-center gap-2 text-left"
                >
                  {open ? <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" aria-hidden /> : <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />}
                  <span className="text-sm font-semibold text-gray-800">{m.name}</span>
                  <span className="text-[11px] text-gray-400">{msCount} task{msCount === 1 ? '' : 's'}</span>
                  {msLoading ? <Loader2 className="h-3 w-3 shrink-0 animate-spin text-gray-400" aria-hidden /> : null}
                  {/* PLANNING CURSOR / APPROVED chips (§2.5). */}
                  {planApproved ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-emerald-200">
                      <CheckCircle className="h-3 w-3" aria-hidden /> plan approved
                    </span>
                  ) : isCursor ? (
                    <span className="rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700 ring-1 ring-indigo-200">
                      planning now
                    </span>
                  ) : null}
                  {/* AWAITING APPROVAL chip (§13 G3.5): synthesizer finished this milestone's plan —
                      prompt the human's per-milestone Approve action. */}
                  {awaitingApproval ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-200"
                      title="The plan for this milestone is ready — review and approve it below">
                      <Clock className="h-3 w-3" aria-hidden /> awaiting your approval
                    </span>
                  ) : null}
                  {/* GROOM SELECTION chip (§2.6e): this milestone has tasks picked for a surgical
                      groom → the composer's Plan Send becomes a groom of exactly these. */}
                  {groomSel.milestone === m.id && groomSel.ids.size ? (
                    <span className="rounded-full bg-fuchsia-50 px-1.5 py-0.5 text-[10px] font-medium text-fuchsia-700 ring-1 ring-fuchsia-200">
                      {groomSel.ids.size} to refine — type your change + Send in Plan
                    </span>
                  ) : null}
                  {/* PLAN-DRIFT (no warning panels): the planner FIXES drift; we only ever show the
                      POSITIVE "plan verified" tick. Unconnected handoffs are auto-stitched in the current
                      milestone, aligned in future ones, or parked in the backlog for approved ones —
                      never surfaced here as a scary break count. */}
                  {m.planIntegrity && m.planIntegrity.closed ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-emerald-200"
                      title="Plan verified: every task handoff connects end-to-end">
                      <CheckCircle className="h-3 w-3" aria-hidden /> plan verified
                    </span>
                  ) : null}
                  {m.exitCriterion ? (
                    <span className="mx-2 hidden min-w-0 flex-1 truncate text-[11px] text-gray-400 sm:block" title={m.exitCriterion}>
                      {m.exitCriterion}
                    </span>
                  ) : null}
                </button>
                {/* HEADER PAGINATOR (right-aligned): narrow the VIEW to N tasks at a time — pure
                    task-count window (disconnected from waves). << first · < prev · page/total · >
                    next · >> last · [N] page-size input · All. Only when expanded + more than a page. */}
                {open && paginated ? (() => {
                  // Icon paginator — proper h-7 tap targets (was tiny « ‹ › » glyphs). first · prev ·
                  // page/total · next · last · [size] · All.
                  const _btn = 'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent';
                  return (
                  <span
                    className="ml-auto flex shrink-0 items-center gap-0.5"
                    data-testid={`paginator-${m.id}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button type="button" data-testid={`page-first-${m.id}`} disabled={page === 0}
                      onClick={() => setPage(0)} title="First page" aria-label="First page" className={_btn}>
                      <ChevronsLeft className="h-4 w-4" aria-hidden />
                    </button>
                    <button type="button" data-testid={`page-prev-${m.id}`} disabled={page === 0}
                      onClick={() => setPage(page - 1)} title="Previous page" aria-label="Previous page" className={_btn}>
                      <ChevronLeft className="h-4 w-4" aria-hidden />
                    </button>
                    <span className="px-1.5 text-[11px] tabular-nums text-gray-500" data-testid={`page-indicator-${m.id}`}>
                      {page + 1}<span className="text-gray-300"> / </span>{pageCount}
                    </span>
                    <button type="button" data-testid={`page-next-${m.id}`} disabled={page >= pageCount - 1}
                      onClick={() => setPage(page + 1)} title="Next page" aria-label="Next page" className={_btn}>
                      <ChevronRight className="h-4 w-4" aria-hidden />
                    </button>
                    <button type="button" data-testid={`page-last-${m.id}`} disabled={page >= pageCount - 1}
                      onClick={() => setPage(pageCount - 1)} title="Last page" aria-label="Last page" className={_btn}>
                      <ChevronsRight className="h-4 w-4" aria-hidden />
                    </button>
                    <span className="mx-1 h-4 w-px bg-gray-200" aria-hidden />
                    <input
                      type="number" min={1} data-testid={`page-size-${m.id}`}
                      value={pageSize === Infinity ? '' : pageSize}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        if (Number.isFinite(n) && n >= 1) setSize(n);
                      }}
                      title="Tasks per page"
                      aria-label="Tasks per page"
                      className="h-7 w-11 rounded-md border border-gray-200 bg-white px-1 text-center text-[11px] text-gray-700 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400/40"
                    />
                    <button type="button" data-testid={`page-all-${m.id}`}
                      onClick={() => setSize(pageSize === Infinity ? DEFAULT_PAGE_SIZE : Infinity)}
                      title={pageSize === Infinity ? 'Back to paged view' : 'Show all tasks'}
                      className={`ml-0.5 inline-flex h-7 items-center rounded-md px-2 text-[11px] font-medium transition-colors ${pageSize === Infinity ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200' : 'text-indigo-600 hover:bg-indigo-50'}`}>
                      All
                    </button>
                  </span>
                  );
                })() : null}
                {/* PLAN TIER dropdown (PLAN-INTEGRATION.md §2.5): choose how this milestone is planned —
                    Auto (architect hint + backstop), Standard (Tier 1), or Verified (Tier 2, prove the
                    plan connects end-to-end). Only on the milestone the NEXT plan run scopes to (the
                    cursor), for managers pre-finalize. Downgrading to Standard when the tasks declare a
                    handoff chain surfaces a warning (a detected chain would otherwise auto-upgrade). */}
                {canManage && !frozen && m.id && isCursor ? (() => {
                  const hasChain = (() => {
                    const prov = new Set(); const req = new Set();
                    for (const t of tasks) {
                      for (const c of (t.provides || [])) { if (c && c.key) prov.add(c.key); }
                      for (const c of (t.requires || [])) { if (c && c.key) req.add(c.key); }
                    }
                    return [...req].some((k) => prov.has(k));
                  })();
                  return (
                    <span className="inline-flex shrink-0 items-center gap-1">
                      <select
                        data-testid="plan-tier-select"
                        value={planTier}
                        onChange={(e) => onPlanTier?.(e.target.value)}
                        className="rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] text-gray-700"
                        title="How to plan this milestone (Verified proves the plan connects end-to-end)"
                      >
                        <option value="auto">Auto</option>
                        <option value="standard">Standard</option>
                        <option value="verified">Verified</option>
                      </select>
                      {hasChain && planTier === 'standard' ? (
                        <span data-testid="tier-downgrade-warning"
                          className="text-[10px] font-medium text-amber-600"
                          title="This milestone has cross-task handoffs — Standard won't verify they connect">
                          ⚠ handoffs unverified
                        </span>
                      ) : null}
                    </span>
                  );
                })() : null}
                {/* APPROVE / REOPEN this milestone's plan — advances the planning cursor. Only for
                    managers on a pre-finalize (still-planning) project; enabled once detailed. */}
                {/* LOCK / UNLOCK (P5a) — after approval; the chip shows who locked it. */}
                {msLocked ? (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-green-50 px-1.5 py-0.5 text-[10px] font-medium text-green-700 ring-1 ring-green-200"
                        data-testid={`tasks-locked-${m.id}`}
                        title={`Locked${m.lockedBy ? ` by ${_actorLabel(m.lockedBy, resolveUser)}` : ''}${m.lockedAt ? ` · ${_fmtTime(m.lockedAt)}` : ''} — its tasks can run and are frozen`}>
                    <Lock className="h-3 w-3" aria-hidden /> locked
                  </span>
                ) : null}
                {m.waitingCount ? (
                  <span className="inline-flex shrink-0 items-center rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-200"
                        data-testid={`tasks-waiting-${m.id}`}
                        title="These tasks can't start until something only you can provide is answered — see Project map → Gaps. The rest of the milestone keeps moving.">
                    {m.waitingCount} waiting on you
                  </span>
                ) : null}
                {canManage && m.id && msLocked ? (
                  <button type="button" onClick={() => onLock(m.id, false)} disabled={busy}
                          data-testid={`tasks-unlock-${m.id}`}
                          className="shrink-0 rounded-md px-2 py-1 text-[11px] font-medium text-amber-700 transition-colors hover:bg-amber-50 disabled:opacity-50"
                          title="Unlock to edit this milestone's tasks again (runnable tasks stay runnable)">
                    Unlock
                  </button>
                ) : null}
                {canManage && m.id && !frozen && planApproved ? (
                  <button type="button" onClick={() => onLock(m.id, true)} disabled={busy}
                          data-testid={`tasks-lock-${m.id}`}
                          className="shrink-0 rounded-md bg-green-600 px-2 py-1 text-[11px] font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-50"
                          title="Lock this milestone to run it: its tasks become runnable and are frozen, and its Timeline is saved as the baseline. Needs every task ready.">
                    Lock
                  </button>
                ) : null}
                {canManage && !frozen && m.id ? (
                  planApproved ? (
                    <button
                      type="button"
                      onClick={() => onApprovePlan(m.id, false)}
                      disabled={busy}
                      className="shrink-0 rounded-md px-2 py-1 text-[11px] font-medium text-amber-700 transition-colors hover:bg-amber-50 disabled:opacity-50"
                      title="Reopen this milestone for re-planning"
                    >
                      Reopen
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onApprovePlan(m.id, true)}
                      disabled={busy || !hasDetailed}
                      className="shrink-0 rounded-md bg-indigo-600 px-2 py-1 text-[11px] font-medium text-white transition-colors hover:bg-indigo-700 disabled:opacity-40"
                      title={hasDetailed ? 'Approve this milestone’s plan → open the next for planning'
                        : 'Detail this milestone’s tasks first (run plan mode / edit) before approving'}
                    >
                      Approve plan
                    </button>
                  )
                ) : null}
              </div>
              {lockBlock[m.id] ? (
                <div className="border-t border-rose-100 bg-rose-50 px-3 py-2 text-[11px] text-rose-800" data-testid={`tasks-lock-refused-${m.id}`}>
                  <div className="font-medium">Not locked — {lockBlock[m.id].message}.</div>
                  {(lockBlock[m.id].exceptions || []).length ? (
                    <ul className="mt-1 space-y-0.5">
                      {lockBlock[m.id].exceptions.slice(0, 12).map((x) => (
                        <li key={x.taskId} data-testid="tasks-lock-exception">
                          <span className="font-mono">{x.taskId}</span> {x.title ? `${x.title} — ` : ''}
                          {(x.reasons || []).join(' · ') || x.verdict}
                        </li>
                      ))}
                      {lockBlock[m.id].exceptions.length > 12 ? <li>… and {lockBlock[m.id].exceptions.length - 12} more</li> : null}
                    </ul>
                  ) : null}
                  {(lockBlock[m.id].needs || []).length ? (
                    <ul className="mt-1 space-y-0.5" data-testid={`tasks-lock-needs-${m.id}`}>
                      {lockBlock[m.id].needs.slice(0, 8).map((x) => (
                        <li key={x.needId}>• {x.title}{x.status === 'failing' ? ' (connection failing)' : ''}</li>
                      ))}
                      <li className="text-rose-600">Provide them in Project map → Gaps, or mark them Not needed there.</li>
                    </ul>
                  ) : null}
                  <div className="mt-1.5 flex gap-1">
                    {(lockBlock[m.id].exceptions || []).length && !(lockBlock[m.id].needs || []).length ? (
                      <button type="button" onClick={() => onLock(m.id, true, true)} disabled={busy}
                              data-testid={`tasks-lock-anyway-${m.id}`}
                              className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50">
                        Lock anyway ({lockBlock[m.id].exceptions.length} exception{lockBlock[m.id].exceptions.length === 1 ? '' : 's'})
                      </button>
                    ) : null}
                    <button type="button" onClick={() => setLockBlock((prev) => { const n = { ...prev }; delete n[m.id]; return n; })}
                            className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors text-rose-700 hover:bg-rose-100">
                      Dismiss
                    </button>
                  </div>
                </div>
              ) : null}
              {open ? (
                <>
                  {/* PLAN TOO LARGE banner (MILESTONE-SCOPED-PLANNING §truncation): the last plan run's
                      output was cut off — the milestone is too big to generate in one pass, so NOTHING
                      was applied (rejected wholesale, never half-applied). Tell the user + offer a CTA
                      that seeds a wave-0 selection so their next Plan Send is bounded. Waves must exist
                      (>1) for the CTA to help; else it's just the message. */}
                  {m.planTooLarge ? (() => {
                    const waves = [...new Set(tasks.map((t) => Number(t.wave) || 0))].sort((a, b) => a - b);
                    const canWave = canManage && !frozen && m.id && waves.length > 1;
                    return (
                      <div className="flex flex-wrap items-center gap-2 border-t border-amber-100 bg-amber-50/60 px-3 py-2"
                        data-testid={`plan-too-large-${m.id}`}>
                        <span className="text-[11px] font-medium text-amber-800">
                          ⚠ This milestone is too large to plan in one pass — the last plan run was cut off and NOT applied.
                          {canWave ? ' Plan it in waves instead.' : ' Break it into smaller milestones or groom a subset.'}
                        </span>
                        {canWave ? (
                          <button
                            type="button"
                            data-testid={`plan-in-waves-${m.id}`}
                            onClick={() => toggleWave(m.id, waves[0])}
                            className="rounded-md bg-amber-600 px-2 py-0.5 text-[11px] font-medium text-white transition-colors hover:bg-amber-700"
                            title={`Select wave ${waveLabel(waves[0], waves)} → then Send in Plan to plan just that wave`}
                          >
                            Plan wave {waveLabel(waves[0], waves)} first
                          </button>
                        ) : null}
                        {/* AUTO: plan every wave in sequence, no per-wave clicks (§wave-auto). */}
                        {canWave && typeof onPlanAllWaves === 'function' ? (
                          <button
                            type="button"
                            data-testid={`plan-all-waves-banner-${m.id}`}
                            onClick={() => onPlanAllWaves(m.id)}
                            className="rounded-md px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-amber-300 transition-colors hover:bg-amber-100"
                            title="Plan every wave automatically, in order — runs server-side, no per-wave clicks"
                          >
                            Plan all waves (auto)
                          </button>
                        ) : null}
                      </div>
                    );
                  })() : null}
                  {/* AUTO-WAVE PROGRESS (§wave-auto): a chain is running for this milestone — show
                      which wave + a Stop. Server-driven, so it keeps advancing even across reloads;
                      the §tab-persist quiet refresh surfaces progress. */}
                  {m.autoWavePlanning ? (() => {
                    const aw = m.autoWavePlanning;
                    const total = (aw.waves || []).length;
                    return (
                      <div className="flex flex-wrap items-center gap-2 border-t border-indigo-100 bg-indigo-50/50 px-3 py-1.5"
                        data-testid={`auto-wave-progress-${m.id}`}>
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-indigo-500 animate-pulse" aria-hidden />
                          Auto-planning wave {Math.min((aw.cursor || 0) + 1, total)} of {total}…
                        </span>
                        {canManage && typeof onStopAutoWave === 'function' ? (
                          <button
                            type="button"
                            data-testid={`stop-auto-wave-${m.id}`}
                            onClick={() => onStopAutoWave(m.id)}
                            className="rounded-md px-2 py-0.5 text-[11px] font-medium text-red-600 transition-colors hover:bg-red-50"
                            title="Stop the auto-wave sequence (an in-flight wave finishes but won't advance)"
                          >
                            Stop
                          </button>
                        ) : null}
                      </div>
                    );
                  })() : null}
                  {/* PLAN-DRIFT (2026-09-18): the three integrity WARNING panels — "Plan integrity —
                      unconnected handoffs" (breaks), "Cross-milestone dependencies", and "Removed
                      dependencies on missing tasks" — were REMOVED. The planner now FIXES drift instead
                      of reporting it: current-milestone breaks are auto-stitched, future milestones are
                      coarsely aligned, and gaps that touch an APPROVED (immutable) milestone go to the
                      backlog for the holistic Reconcile (Project map tab). The user sees a connected plan
                      + a positive "plan verified" tick, never a scary fault list. (PLAN-DRIFT.md) */}
                  {/* EPIC EXPANSION strip REMOVED (2026-09-17): it was the pre-auto-decompose MANUAL
                      "Create tasks per epic" affordance. f.synth now decomposes epics into subtasks
                      automatically, so the strip was redundant + confusing ("all epics broken down ✓ —
                      pick one to add more subtasks"). Adding more subtasks is just normal grooming
                      (row-select / Plan). The epic→subtask tree renders nested in the table below. */}
                  {/* WAVE REFINE (§2.8 / §13 G3.5) — POST-APPROVAL only. A wave is a group of PARALLEL
                      tasks; the per-wave chips are a SURGICAL REFINE affordance (re-plan ONE wave), the
                      multi-task sibling of per-task row-select — so they're gated on ``planApproved``
                      exactly like row-select. BEFORE approval there is NO wave block: you plan the WHOLE
                      milestone via the composer, and the ONLY pre-approval wave path is the "plan too
                      large" banner above (shown solely when a milestone can't be planned in one pass).
                      Only for a detailed, pre-finalize, APPROVED milestone with >1 wave. */}
                  {canManage && !frozen && m.id && hasDetailed && planApproved && (() => {
                    const waves = [...new Set(tasks.map((t) => Number(t.wave) || 0))].sort((a, b) => a - b);
                    if (waves.length <= 1) return null;
                    const active = groomSel.milestone === m.id ? groomSel.waves : new Set();
                    return (
                      <div className="flex flex-wrap items-center gap-1 border-t border-gray-100 bg-fuchsia-50/30 px-3 py-1.5">
                        <span className="mr-1 text-[10px] font-medium uppercase tracking-wide text-fuchsia-500">Refine waves</span>
                        {/* (L122) Waves are 1-based in DATA (derive_waves); legacy 0-based plans still read 1-based. */}
                        {waves.map((w) => (
                          <button
                            key={w}
                            type="button"
                            data-testid={`groom-wave-${m.id}-${w}`}
                            onClick={() => toggleWave(m.id, w)}
                            className={`rounded-md px-2 py-0.5 text-[11px] font-medium ring-1 transition-colors ${active.has(w) ? 'bg-fuchsia-600 text-white ring-fuchsia-600' : 'bg-white text-fuchsia-700 ring-fuchsia-200 hover:bg-fuchsia-100'}`}
                            title={`Refine only wave ${waveLabel(w, waves)} of ${m.name} (Send in Plan mode)`}
                          >
                            {waveLabel(w, waves)}
                          </button>
                        ))}
                        {active.size ? (
                          <span className="ml-1 text-[10px] text-fuchsia-600">
                            wave{active.size === 1 ? '' : 's'} {[...active].sort((a, b) => a - b).map((w) => waveLabel(w, waves)).join(', ')} — type your change + Send in Plan
                          </span>
                        ) : null}
                      </div>
                    );
                  })()}
                  <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-left">
                    <thead>
                      <tr className="border-t border-gray-100 bg-white">
                        {COLS.map((c) => (
                          <th key={c.key} className={`px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 ${c.cls}`}>
                            {w.expert && c.key === 'team' ? 'Agents' : c.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.length > 0
                        ? visibleTasks.flatMap((t) => {
                          const kids = kidsByParent[t.id] || [];
                          const kidsOpen = !foldedKids.has(t.id);
                          const rowFor = (task, isChild) => (
                            <TaskRow
                              key={task.id} task={task} canManage={canManage} finalized={frozen}
                              onSave={onSave} onAttest={handleAttest}
                              // REFINE select — gated on milestone plan APPROVAL (§13 G3.5). The planner
                              // grooms the WHOLE milestone until the human APPROVES its plan; only AFTER
                              // approval do we allow SURGICAL per-task / multi-task refine (pick 001, 002).
                              // Before approval the milestone offers whole-milestone Plan (wave chips /
                              // Send), never row-select — you refine a plan that's been accepted, not one
                              // still being planned. Pre-finalize + editable (draft/pending) rows only.
                              selectable={(canManage && !frozen && m.id && planApproved
                                && (task.status === 'draft' || task.status === 'pending'))
                                // RUN TASKS (M3): after lock, pick pending tasks to run in order (never one
                                // waiting on you — the server would skip it anyway)
                                || (!!m.id && frozen && task.status === 'pending' && !(task.waitingOn || []).length)}
                              selectForRun={frozen}
                              selected={groomSel.milestone === m.id && groomSel.ids.has(task.id)}
                              onToggleSelect={(tid) => toggleGroom(m.id, tid)}
                              // SUBTASK NESTING (§0.1): parent rows fold their children; children indent.
                              childCount={isChild ? 0 : kids.length}
                              kidsOpen={kidsOpen}
                              onToggleKids={toggleKids}
                              isChild={isChild}
                              onLoadMd={loadTaskMd}
                              onOpenPlanDoc={onOpenPlanDoc}
                              // PROVENANCE (§prov): row-level facts off the task + the MILESTONE-level
                              // approval off ``m`` — scoped correctly, labeled correctly in the badge.
                              prov={{
                                updatedBy: task.updatedBy || '',
                                updatedAt: task.updatedAt || '',
                                rev: task.rev || 0,
                                authoredRunId: task.authoredRunId || '',
                                approvedBy: m.approvedBy || '',
                                approvedAt: m.approvedAt || '',
                              }}
                              resolveUser={resolveUser}
                            />
                          );
                          return [rowFor(t, false), ...(kidsOpen ? kids.map((k) => rowFor(k, true)) : [])];
                        })
                        : (
                          <tr><td colSpan={COLS.length} className="px-3 py-3 text-[11px] text-gray-400">No {w.leavesLc} in this {w.groupLc} yet.</td></tr>
                        )}
                      {/* Pagination lives in the HEADER (right-aligned « ‹ ›/total › » [N] All) — the
                          old footer "Show more" is removed. */}
                    </tbody>
                  </table>
                  </div>
                </>
              ) : null}

              {open && canManage && !frozen ? (
                <div className="border-t border-gray-100 px-3 py-2">
                  {adding === m.name ? (
                    <div className="max-w-xl space-y-1.5 rounded-md border border-dashed border-gray-300 p-2">
                      <input
                        value={newTask.title}
                        onChange={(e) => setNewTask((n) => ({ ...n, title: e.target.value }))}
                        placeholder="Task title"
                        className="w-full rounded border border-gray-300 px-2 py-1 text-[12px]"
                      />
                      <input
                        value={newTask.leadAgent}
                        onChange={(e) => setNewTask((n) => ({ ...n, leadAgent: e.target.value }))}
                        placeholder="Lead agent id"
                        className="w-full rounded border border-gray-300 px-2 py-1 text-[12px]"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => submitAdd(m.name)}
                          disabled={busy}
                          className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                          Add task
                        </button>
                        <button
                          type="button"
                          onClick={() => setAdding(null)}
                          className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors text-gray-600 hover:bg-gray-100"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setAdding(m.name)}
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:underline"
                    >
                      <Plus className="h-3 w-3" aria-hidden /> Add task
                    </button>
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
